import { Modal } from '@/components';
import { useUpdateSite } from '@/remote/site-management/update-site';
import type { Site } from '@/types/site';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  BasicInfoTab,
  LocationInfoTab,
  NotificationSettingsTab,
  PipelineInfoTab,
  WaterInfoTab,
} from '../tabs';
import { siteEditValidationSchema } from './validation';

interface SiteEditModalProps {
  isOpen: boolean;
  site: Site | null;
  onClose: () => void;
}

type TabType =
  | 'basic'
  | 'location'
  | 'water'
  | 'facility'
  | 'pipeline'
  | 'investment'
  | 'notification';

export default function SiteEditModal({
  isOpen,
  site,
  onClose,
}: Readonly<SiteEditModalProps>) {
  const updateSiteMutation = useUpdateSite();

  const [activeTab, setActiveTab] = useState<TabType>('basic');

  // React Hook Form 설정
  const methods = useForm<Site>({
    resolver: yupResolver<Site, unknown, unknown>(siteEditValidationSchema),
    mode: 'all', // 모든 이벤트에서 validation (onChange, onBlur, onSubmit)
    reValidateMode: 'onChange', // 재검증도 onChange에서 실행
  });

  const {
    handleSubmit,
    reset,
    setValue,
    trigger,
    formState: { errors, isSubmitting, isDirty, isValid },
    watch,
  } = methods;

  // 현재 form 데이터 watch
  const formData = watch();

  useEffect(() => {
    // alias 값이 없으면 기본값 설정
    if (!formData.alias) {
      setValue(
        'alias',
        {
          circuitBreaker: '차단기',
          pressureSensor: '압력센서',
          external: '외부',
          preparation: '예비1',
          preparation2: '예비2',
        },
        {
          shouldValidate: true,
          shouldDirty: false, // 기본값 설정이므로 dirty로 표시하지 않음
          shouldTouch: false,
        }
      );
    }
  }, [formData, setValue]);

  const handleSaveEdit = async (data: Site) => {
    const formData = data;
    if (formData) {
      try {
        await updateSiteMutation.mutateAsync({
          siteId: formData.siteId,
          siteData: {
            name: formData.name,
            cdmaNumber: formData.cdmaNumber,
            managementNumber: formData.managementNumber,
            wellPosition: formData.wellPosition,
            waterSupplyPosition: formData.waterSupplyPosition,
            managementPosition: formData.managementPosition,
            managementType: formData.managementType,
            facilityType: formData.facilityType,
            waterIntakeType: formData.waterIntakeType,
            installedDate: formData.installedDate,
            motorSpecification: formData.motorSpecification,
            tankSize: formData.tankSize,
            waterSupplyInfo: formData.waterSupplyInfo,
            intakeFacilityInfo: formData.intakeFacilityInfo,
            purifyFacilityInfo: formData.purifyFacilityInfo,
            waterSupplyPipelineInfo: formData.waterSupplyPipelineInfo,
            drainageSupplyPipelineInfo: formData.drainageSupplyPipelineInfo,
            facilityPlan: formData.facilityPlan,
            waterSource: formData.waterSource,
            investmentInfos: formData.investmentInfos,
            alias: formData.alias,
            motorActivateCount: formData.motorActivateCount,
            lowWaterLevel: formData.lowWaterLevel,
            highWaterLevel: formData.highWaterLevel,
            notificationManagerIds: formData.notificationManagerIds,
            electricWaveErrorSmsEnabled: formData.electricWaveErrorSmsEnabled,
            overloadErrorSmsEnabled: formData.overloadErrorSmsEnabled,
            undergroundWaterErrorSmsEnabled:
              formData.undergroundWaterErrorSmsEnabled,
            waterLevelSmsEnabled: formData.waterLevelSmsEnabled,
          },
        });
        onClose();
      } catch (error) {
        console.error('사이트 수정 실패:', error);
        // 에러가 발생해도 모달은 닫지 않아서 사용자가 다시 시도할 수 있도록 함
      }
    }
  };

  // 사이트 데이터 변경 핸들러 (기존 탭들을 위한 호환성 유지)
  const handleSiteChange = (updatedSite: Site) => {
    Object.keys(updatedSite).forEach((key) => {
      setValue(key as keyof Site, updatedSite[key as keyof Site], {
        shouldValidate: true, // 값 변경 시 즉시 validation 트리거
        shouldDirty: true,
        shouldTouch: true,
      });
    });
  };

  // site prop이 변경될 때 form을 초기화
  useEffect(() => {
    if (site && isOpen) {
      reset(site);
      // form 초기화 후 validation 트리거
      setTimeout(() => {
        trigger();
      }, 0);
    }
  }, [site, isOpen, reset, trigger]);

  // 모달이 닫힐 때 form 상태 초기화
  const handleClose = () => {
    if (site) {
      reset(site);
    }
    setActiveTab('basic');
    onClose();
  };

  const onSubmit = async (data: Site) => {
    if (!isDirty) {
      alert('변경된 내용이 없습니다.');
      return;
    }

    try {
      await handleSaveEdit(data);
      alert('현장 정보가 성공적으로 수정되었습니다.');
      handleClose();
    } catch (error) {
      console.error('현장 정보 수정 실패:', error);
      alert('현장 정보 수정 중 오류가 발생했습니다.');
    }
  };

  // 필수 필드 에러 확인
  const hasRequiredFieldErrors = () => {
    return !!(
      errors.siteId ||
      errors.name ||
      errors.cdmaNumber ||
      errors.wellPosition?.address ||
      errors.wellPosition?.name ||
      errors.wellPosition?.latitude ||
      errors.wellPosition?.longitude
    );
  };

  // 탭별 에러 표시를 위한 함수들
  const getTabErrorStatus = (tab: TabType): boolean => {
    switch (tab) {
      case 'basic':
        return !!(errors.siteId || errors.name || errors.cdmaNumber);
      case 'location':
        return !!(
          errors.wellPosition?.name ||
          errors.wellPosition?.address ||
          errors.wellPosition?.latitude ||
          errors.wellPosition?.longitude ||
          errors.waterSupplyPosition
        );
      case 'water':
        return !!errors.waterSupplyInfo;
      case 'facility':
        return !!(
          errors.intakeFacilityInfo ||
          errors.purifyFacilityInfo ||
          errors.facilityPlan
        );
      case 'pipeline':
        return !!(
          errors.waterSupplyPipelineInfo || errors.drainageSupplyPipelineInfo
        );
      case 'investment':
        return !!errors.investmentInfos;
      case 'notification':
        return !!(
          errors.lowWaterLevel ||
          errors.highWaterLevel ||
          errors.notificationManagerIds ||
          errors.electricWaveErrorSmsEnabled ||
          errors.overloadErrorSmsEnabled ||
          errors.undergroundWaterErrorSmsEnabled
        );
      default:
        return false;
    }
  };

  if (!site) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="현장 정보 수정"
      size="4xl"
      maxHeight="max-h-[95vh]"
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className="p-6">
          {/* 탭 네비게이션 */}
          <div className="flex flex-wrap border-b border-gray-200 mb-6">
            {[
              { id: 'basic', label: '기본정보' },
              { id: 'location', label: '위치정보' },
              { id: 'water', label: '급수정보' },
              // { id: 'facility', label: '시설정보' },
              { id: 'pipeline', label: '배관정보' },
              // { id: 'investment', label: '투자정보' },
              { id: 'notification', label: '알림설정' },
            ].map((tab) => {
              const hasError = getTabErrorStatus(tab.id as TabType);
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id as TabType)}
                  className={`px-4 py-2 text-sm font-medium rounded-t-lg relative ${
                    activeTab === tab.id
                      ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                  }`}
                >
                  {tab.label}
                  {/* 에러 표시 */}
                  {hasError && (
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                  )}
                </button>
              );
            })}
          </div>

          {/* 탭 컨텐츠 */}
          <div className="min-h-[400px]">
            {/* 기본정보 탭 - react-hook-form 사용 */}
            {activeTab === 'basic' && <BasicInfoTab isEditMode />}

            {/* 위치정보 탭 - 기존 방식 유지 */}
            {activeTab === 'location' && (
              <LocationInfoTab
                site={formData}
                onSiteChange={handleSiteChange}
              />
            )}

            {/* 급수정보 탭 - 기존 방식 유지 */}
            {activeTab === 'water' && (
              <WaterInfoTab site={formData} onSiteChange={handleSiteChange} />
            )}

            {/* 시설정보 탭 - 기존 방식 유지 */}
            {/* {activeTab === 'facility' && (
              <FacilityInfoTab
                site={formData}
                onSiteChange={handleSiteChange}
              />
            )} */}

            {/* 배관정보 탭 - 기존 방식 유지 */}
            {activeTab === 'pipeline' && (
              <PipelineInfoTab
                site={formData}
                onSiteChange={handleSiteChange}
              />
            )}

            {/* 투자정보 탭 - 기존 방식 유지 */}
            {/* {activeTab === 'investment' && (
              <InvestmentInfoTab
                site={formData}
                onSiteChange={handleSiteChange}
              />
            )} */}

            {/* 알림설정 탭 - react-hook-form 사용 */}
            {activeTab === 'notification' && (
              <NotificationSettingsTab isEditMode />
            )}
          </div>

          {/* 에러 메시지 표시 */}
          {hasRequiredFieldErrors() && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">
                필수 항목을 모두 입력해주세요:
              </p>
              <ul className="mt-1 text-xs text-red-500 list-disc list-inside">
                {errors.siteId && <li>{errors.siteId.message}</li>}
                {errors.name && <li>{errors.name.message}</li>}
                {errors.cdmaNumber && <li>{errors.cdmaNumber.message}</li>}
                {errors.wellPosition?.name && (
                  <li>관정명: {errors.wellPosition.name.message}</li>
                )}
                {errors.wellPosition?.address && (
                  <li>관정 주소: {errors.wellPosition.address.message}</li>
                )}
                {errors.wellPosition?.latitude && (
                  <li>관정 위도: {errors.wellPosition.latitude.message}</li>
                )}
                {errors.wellPosition?.longitude && (
                  <li>관정 경도: {errors.wellPosition.longitude.message}</li>
                )}
                {errors.managementPosition?.name && (
                  <li>관리소명: {errors.managementPosition.name.message}</li>
                )}
                {errors.managementPosition?.address && (
                  <li>
                    관리소 주소: {errors.managementPosition.address.message}
                  </li>
                )}
                {errors.managementPosition?.latitude && (
                  <li>
                    관리소 위도: {errors.managementPosition.latitude.message}
                  </li>
                )}
                {errors.managementPosition?.longitude && (
                  <li>
                    관리소 경도: {errors.managementPosition.longitude.message}
                  </li>
                )}
              </ul>
            </div>
          )}

          {/* 변경 사항 알림 */}
          {isDirty && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-700">
                변경된 내용이 있습니다. 저장하지 않으면 변경사항이 사라집니다.
              </p>
            </div>
          )}

          {/* 버튼 영역 */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className={`px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors ${
                isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              취소
            </button>
            <button
              type="submit"
              disabled={!isValid || !isDirty}
              className={`px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center ${
                !isValid || !isDirty ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isSubmitting ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-3 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  저장 중...
                </>
              ) : (
                '변경사항 저장'
              )}
            </button>
          </div>
        </form>
      </FormProvider>
    </Modal>
  );
}
